import React from 'react';
import * as S from './styles';

export interface PageControlProps {
  currentPage: number;
  totalPages: number;
  onChange: (page: number) => void;
  /** Pages to show before and after the current page — default 2 */
  delta?: number;
  showPrevNext?: boolean; // default = true
  syncPageWithJQuery?: boolean; // default = false
  /** @deprecated no longer used; kept for backward compatibility */
  windowSize?: number;
}

/**
 * Builds the list of page numbers (and ellipsis markers) to render.
 *
 * Rules:
 *  - Page 1 is always present.
 *  - `delta` pages before and after `current` are always shown.
 *  - When on page 1, the range extends to `current + delta + 1` (shows up to page 4).
 *  - Gaps larger than 1 between page 1 and the range render an ellipsis.
 */
function buildPageList(
  current: number,
  total: number,
  delta: number
): (number | 'ellipsis-left' | 'ellipsis-right')[] {
  if (total <= 0) return [];
  if (total === 1) return [1];

  // On page 1 show one extra page ahead to compensate for having no "before" pages
  const forwardDelta = current === 1 ? delta + 1 : delta;

  const rangeStart = Math.max(1, current - delta);
  const rangeEnd = Math.min(total, current + forwardDelta);

  const items: (number | 'ellipsis-left' | 'ellipsis-right')[] = [];

  // 1. Always include page 1
  items.push(1);

  // 2. Ellipsis between page 1 and the start of the range (when gap > 1)
  if (rangeStart > 2) {
    items.push('ellipsis-left');
  }

  // 3. Range around the current page — skip 1 to avoid duplicates
  for (let p = rangeStart; p <= rangeEnd; p++) {
    if (p !== 1) {
      items.push(p);
    }
  }

  // 4. Ellipsis between the end of the range and the last page (when gap > 1)
  if (rangeEnd < total - 1) {
    items.push('ellipsis-right');
  }

  return items;
}

const PageControl: React.FC<PageControlProps> = ({
  currentPage,
  totalPages,
  onChange,
  delta = 2,
  showPrevNext = true,
  syncPageWithJQuery = false
}) => {
  const handleOnChange = (page: number) => {
    if (syncPageWithJQuery) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).$page = page;
      const url = new URL(window.location.href);
      url.searchParams.set('page', page.toString());
      window.history.pushState({}, '', url.toString());
    }
    onChange(page);
  };

  const pageList = buildPageList(currentPage, totalPages, delta);

  if (totalPages <= 0) return null;

  return (
    <S.Pagination>
      {showPrevNext && currentPage > 1 && (
        <button onClick={() => handleOnChange(currentPage - 1)}>
          Anterior
        </button>
      )}

      {pageList.map((item, idx) => {
        if (item === 'ellipsis-left' || item === 'ellipsis-right') {
          return (
            <S.Ellipsis key={`${item}-${idx}`} aria-hidden="true">
              ...
            </S.Ellipsis>
          );
        }
        return (
          <button
            key={item}
            onClick={() => handleOnChange(item)}
            className={currentPage === item ? 'active' : ''}>
            {item}
          </button>
        );
      })}

      {showPrevNext && currentPage < totalPages && (
        <button onClick={() => handleOnChange(currentPage + 1)}>Próxima</button>
      )}
    </S.Pagination>
  );
};

export default PageControl;
